import { useState, useEffect, useCallback, useRef } from "react";
import { useSelector, useDispatch } from "react-redux";
import { getLoggedinUser } from "@/store/slices/auth/authSlice";
import { setGeneralState, getSpecificGeneralInfo } from "@/store/slices/general/generalSlice";
import { useCRMDataClient } from "@/services/data-client/DataClientContext";
import { toast } from "react-toastify";

/**
 * Maps the color property from the API to the internal theme mode
 * The API returns: "default", "dark", "light"
 * Internal modes: "default_theme", "night_theme", "light_theme"
 */
export const ColorToModeMap = {
  default: "default_theme",
  dark: "night_theme",
  light: "light_theme",
};

/**
 * Maps internal theme mode to color property for API
 */
export const ModeToColorMap = {
  default_theme: "default",
  night_theme: "dark",
  light_theme: "light",
};

/**
 * Available theme modes for the UI.
 * These map to the color property from the Frontdoor API.
 */
export const ThemeModes = {
  Default: "default_theme",
  Light: "light_theme",
  Night: "night_theme",
};

/**
 * Default theme mode when no theme is saved or on API error
 */
const DEFAULT_MODE = "default_theme";

/**
 * useFrontdoorTheme Hook
 *
 * Manages user theme preferences using the Frontdoor REST API.
 * Replaces the legacy GraphQL-based user settings approach.
 *
 * API Endpoints:
 * - GET /api/themes - Fetch all available themes
 * - GET /api/themes/user/current - Get current user's theme
 * - PUT /api/themes/user/{themeId} - Save user's theme preference
 *
 * @returns {Object} Theme state and control functions
 */
const useFrontdoorTheme = () => {
  const dispatch = useDispatch();
  const user = useSelector(getLoggedinUser);
  const themeInfo = useSelector(getSpecificGeneralInfo("theme"));
  const availableThemes = useSelector(getSpecificGeneralInfo("availableThemes"));
  const { client: dataClient } = useCRMDataClient();

  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [themesLoaded, setThemesLoaded] = useState(false);

  // Prevent duplicate fetches
  const fetchingRef = useRef(false);
  const themesFetchedRef = useRef(false);

  /**
   * Fetch all available themes from the system
   */
  const fetchAllThemes = useCallback(async () => {
    if (!dataClient?.themes || themesFetchedRef.current) return;

    try {
      themesFetchedRef.current = true;
      const themes = await dataClient.themes.getAllThemes();

      if (Array.isArray(themes)) {
        // Map API themes to include internal mode
        const mappedThemes = themes.map((theme) => ({
          ...theme,
          mode: ColorToModeMap[theme.color] || DEFAULT_MODE,
        }));

        dispatch(setGeneralState({ availableThemes: mappedThemes }));
      }
    } catch (err) {
      console.error("[useFrontdoorTheme] Error fetching themes:", err);
      // Don't block the UI if themes can't be fetched
    }
  }, [dataClient?.themes, dispatch]);

  /**
   * Fetch the current user's theme preference
   */
  const fetchUserTheme = useCallback(async () => {
    if (!user?.UserID || !dataClient?.themes || fetchingRef.current) return;

    fetchingRef.current = true;
    setLoading(true);
    setError(null);

    try {
      const userTheme = await dataClient.themes.getUserCurrentTheme();

      if (userTheme && userTheme.themeId) {
        const mode = ColorToModeMap[userTheme.color] || DEFAULT_MODE;

        dispatch(
          setGeneralState({
            theme: {
              mode,
              themeId: userTheme.themeId,
              selectedPrimary: "primary_1",
              selectedSecondary: "primary_1",
              customPrimary: {},
              customSecondary: {},
            },
          })
        );
      } else {
        // No theme set, use default
        dispatch(
          setGeneralState({
            theme: {
              mode: DEFAULT_MODE,
              selectedPrimary: "primary_1",
              selectedSecondary: "primary_1",
              customPrimary: {},
              customSecondary: {},
            },
          })
        );
      }
    } catch (err) {
      console.error("[useFrontdoorTheme] Error fetching user theme:", err);
      // On error, fall back to default theme
      dispatch(
        setGeneralState({
          theme: {
            mode: DEFAULT_MODE,
            selectedPrimary: "primary_1",
            selectedSecondary: "primary_1",
            customPrimary: {},
            customSecondary: {},
          },
        })
      );
      setError(err.message);
    } finally {
      setLoading(false);
      setThemesLoaded(true);
    }
  }, [user?.UserID, dataClient?.themes, dispatch]);

  /**
   * Save the user's theme preference
   * @param {string} themeId - The UUID of the theme to save
   * @param {string} mode - Optional mode (for optimistic UI update)
   */
  const saveUserTheme = useCallback(
    async (themeId, mode = null) => {
      if (!user?.UserID || !dataClient?.themes || !themeId) return;

      setSaving(true);
      setError(null);

      try {
        await dataClient.themes.setUserTheme(themeId);

        // Find the theme in available themes to get the mode
        const selectedTheme = availableThemes?.find((t) => t.themeId === themeId);
        const themeMode = mode || selectedTheme?.mode || DEFAULT_MODE;

        // Update local state after successful save
        dispatch(
          setGeneralState({
            theme: {
              ...themeInfo,
              mode: themeMode,
              themeId,
            },
          })
        );

        return true;
      } catch (err) {
        console.error("[useFrontdoorTheme] Error saving user theme:", err);
        setError(err.message);
        toast.error("Failed to save theme preference. Please try again.");
        return false;
      } finally {
        setSaving(false);
      }
    },
    [user?.UserID, dataClient?.themes, dispatch, themeInfo, availableThemes]
  );

  /**
   * Set theme mode locally (for preview before saving)
   * @param {string} mode - The theme mode to set locally
   */
  const setLocalThemeMode = useCallback(
    (mode) => {
      dispatch(
        setGeneralState({
          theme: {
            ...themeInfo,
            mode,
          },
        })
      );
    },
    [dispatch, themeInfo]
  );

  /**
   * Get theme ID by mode (color)
   * @param {string} mode - Theme mode (default_theme, light_theme, night_theme)
   * @returns {string|null} Theme ID or null if not found
   */
  const getThemeIdByMode = useCallback(
    (mode) => {
      const color = ModeToColorMap[mode];
      const theme = availableThemes?.find((t) => t.color === color);
      return theme?.themeId || null;
    },
    [availableThemes]
  );

  // Fetch all available themes on mount
  useEffect(() => {
    fetchAllThemes();
  }, [fetchAllThemes]);

  // Fetch user's theme preference when user is available
  useEffect(() => {
    if (user?.UserID && !themesLoaded) {
      fetchUserTheme();
    }
  }, [user?.UserID, fetchUserTheme, themesLoaded]);

  return {
    // State
    themeInfo,
    availableThemes,
    loading,
    saving,
    error,
    themesLoaded,

    // Actions
    fetchUserTheme,
    saveUserTheme,
    setLocalThemeMode,
    getThemeIdByMode,
    refreshThemes: fetchAllThemes,
  };
};

export default useFrontdoorTheme;